import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  complianceErrorMessage,
  notifyHseReadChanged,
  postponeDocumentReadRequirement,
} from "../../../../api/hse/documentCompliance";
import { promangToast } from "../../../../operationFeedback";
import { formatComplianceDate, PostponementDots } from "./ComplianceBits";

const DAY_MS = 24 * 60 * 60 * 1000;

/** Postponement choices: 1, 3 and the maximum (e.g. 1 · 3 · 7 days). */
export const postponeChoices = (maxDays) => {
  const max = Math.max(0, Number(maxDays) || 0);
  return [...new Set([1, 3, max].filter((day) => day >= 1 && day <= max))].sort((a, b) => a - b);
};

export const daysUntil = (dueAt, now = Date.now()) => {
  if (!dueAt) return null;
  const time = new Date(dueAt).getTime();
  if (Number.isNaN(time)) return null;
  return Math.ceil((time - now) / DAY_MS);
};

/**
 * The reader's own «lest og forstått» state for one HMS document: deadline, postponements
 * left, confirmation via training, and the confirm / postpone actions.
 * `status` is the API's `current_user_read_status`.
 */
export const ReadRequirementNotice = ({
  documentId,
  status,
  onAcknowledge,
  acknowledging = false,
  canAcknowledge = true,
  onRequirementChange,
  compact = false,
}) => {
  const { t, i18n } = useTranslation();
  const [choosing, setChoosing] = useState(false);
  const [days, setDays] = useState(null);
  const [postponing, setPostponing] = useState(false);
  const [error, setError] = useState(null);
  const requirement = status?.requirement || null;
  const choices = useMemo(() => postponeChoices(requirement?.postponement_max_days), [requirement?.postponement_max_days]);

  if (!status) return null;

  if (status.exempt_as_creator) {
    return (
      <div className="pm-alert" role="status">
        <span className="pm-alert__icon" aria-hidden="true">i</span>
        <div>
          <div className="pm-alert__title">{t("hseCompliance.notice.exempt_title")}</div>
          <div className="pm-alert__desc">{t("hseCompliance.notice.exempt_desc")}</div>
        </div>
      </div>
    );
  }

  if (status.acknowledged_read_understood) {
    const via = status.acknowledged_via;
    return (
      <div className="pm-alert pm-alert--ok" role="status">
        <span className="pm-alert__icon" aria-hidden="true">✓</span>
        <div>
          <div className="pm-alert__title">{t("hseCompliance.notice.done_title")}</div>
          <div className="pm-alert__desc">
            {via === "training"
              ? t("hseCompliance.notice.done_training", { date: formatComplianceDate(status.read_understood_at, i18n.language) })
              : via === "carried_forward"
                ? t("hseCompliance.notice.done_carried")
                : t("hseCompliance.notice.done_direct", { date: formatComplianceDate(status.read_understood_at, i18n.language) })}
          </div>
        </div>
      </div>
    );
  }

  const required = Boolean(status.read_required && requirement && requirement.status === "pending");
  const left = daysUntil(requirement?.due_at);
  const overdue = Boolean(requirement?.overdue);
  const tone = !required ? "" : overdue ? "pm-alert--bad" : "pm-alert--warn";

  const submitPostpone = async () => {
    if (!days || postponing) return;
    setPostponing(true);
    setError(null);
    try {
      const data = await postponeDocumentReadRequirement(documentId, days, requirement?.lock_version);
      promangToast.success(
        t("hseCompliance.notice.postponed", { date: formatComplianceDate(data?.requirement?.due_at, i18n.language) })
      );
      setChoosing(false);
      setDays(null);
      onRequirementChange?.(data?.requirement || null);
      notifyHseReadChanged();
    } catch (err) {
      setError(complianceErrorMessage(err, t("hseCompliance.notice.postpone_error")));
    } finally {
      setPostponing(false);
    }
  };

  const title = !required
    ? t("hseCompliance.notice.optional_title")
    : overdue
      ? t("hseCompliance.notice.overdue_title", { date: formatComplianceDate(requirement.due_at, i18n.language) })
      : t("hseCompliance.notice.due_title", { date: formatComplianceDate(requirement.due_at, i18n.language), count: Math.max(left ?? 0, 0) });

  return (
    <div className={`pm-alert ${tone}`.trim()} role="status">
      <span className="pm-alert__icon" aria-hidden="true">{required ? "!" : "i"}</span>
      <div className="min-w-0 flex-1">
        <div className="pm-alert__title">{title}</div>
        <div className="pm-alert__desc">
          {required ? t("hseCompliance.notice.required_desc") : t("hseCompliance.notice.optional_desc")}
        </div>
        {required && requirement.max_postponements > 0 ? (
          <div className="mt-2 flex flex-wrap items-center gap-2 text-[12.5px] text-[color:var(--pls-muted)]">
            <PostponementDots used={requirement.postponement_count} max={requirement.max_postponements} />
            <span>{t("hseCompliance.notice.postponements_left", { count: requirement.postponements_left })}</span>
          </div>
        ) : null}

        {choosing ? (
          <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center">
            <div className="pm-segmented pm-segmented--sm" role="radiogroup" aria-label={t("hseCompliance.notice.postpone_choose")}>
              {choices.map((day) => (
                <button
                  key={day}
                  type="button"
                  role="radio"
                  aria-checked={days === day}
                  className={`pm-segmented__option${days === day ? " is-active" : ""}`}
                  onClick={() => setDays(day)}
                >
                  {t("hseCompliance.notice.days", { count: day })}
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              <button type="button" className="pm-btn pm-btn--secondary pm-btn--sm" onClick={() => { setChoosing(false); setDays(null); setError(null); }}>
                {t("Avbryt")}
              </button>
              <button
                type="button"
                className="pm-btn pm-btn--secondary pm-btn--sm"
                disabled={!days || postponing}
                aria-busy={postponing || undefined}
                onClick={submitPostpone}
              >
                {postponing ? <span className="pm-btn__spinner" aria-hidden="true" /> : null}
                {days ? t("hseCompliance.notice.postpone_confirm", { count: days }) : t("hseCompliance.notice.postpone_choose")}
              </button>
            </div>
          </div>
        ) : null}

        {error ? <p className="pm-field-error mt-2" role="alert">{error}</p> : null}

        {!choosing ? (
          <div className={`mt-3 flex flex-wrap gap-2 ${compact ? "" : "sm:items-center"}`}>
            {canAcknowledge && onAcknowledge ? (
              <button
                type="button"
                className={`pm-btn ${required ? "pm-btn--primary" : "pm-btn--secondary"} pm-btn--sm`}
                onClick={onAcknowledge}
                disabled={acknowledging}
                aria-busy={acknowledging || undefined}
              >
                {acknowledging ? <span className="pm-btn__spinner" aria-hidden="true" /> : null}
                {t("hse.document.acknowledge_read")}
              </button>
            ) : null}
            {required && requirement.can_postpone ? (
              <button type="button" className="pm-btn pm-btn--ghost pm-btn--sm" onClick={() => setChoosing(true)}>
                {t("hseCompliance.notice.postpone")}
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default ReadRequirementNotice;
